import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SelectionItem } from 'src/app/standalones/components/radio-selection/radio-selection.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { SpaceRepository } from 'src/app/state/space.repository';
import { SpaceLeaderModeService } from 'src/app/services/space-leader-mode.service';
import { FlagsService, FLAGS } from 'src/app/services/flags.service';
import { LocalStorageItemIDs, LocalStorageService } from 'src/app/services/local-storage.service';
import { PDFBoardStrategy } from 'src/app/models/upload';
import { BulkUploadService } from 'src/app/sessions/session/upload/bulk-upload-panel/services/bulk-upload.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-bulk-upload-insertion-strategy',
  templateUrl: './bulk-upload-insertion-strategy.component.html',
  styleUrls: ['./bulk-upload-insertion-strategy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkUploadInsertionStrategyComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedStrategy: PDFBoardStrategy = PDFBoardStrategy.CurrentBoard;
  @Input() uploadAsSeparateImages = true;
  @Output() selectionChange = new EventEmitter<{
    strategy: PDFBoardStrategy;
    uploadAsSeparateImages: boolean;
  }>();

  selectedPagesCount = 0;
  selectedFilesCount = 0;
  headerText = '';
  bodyText = '';
  strategiesSelectionItems: SelectionItem[] = [];

  insertingAsSeparateImagesEnabled$ = this.flagsService.featureFlagChanged(
    FLAGS.RENDER_PDF_AS_SEPARATE_IMAGES,
  );

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly translateService: TranslateService,
    private readonly spaceRepo: SpaceRepository,
    private readonly spaceLeaderModeService: SpaceLeaderModeService,
    private readonly flagsService: FlagsService,
    private readonly localStorageService: LocalStorageService,
    private readonly bulkUploadService: BulkUploadService,
  ) {
    const isFlagEnabled = this.flagsService.isFlagEnabled(FLAGS.RENDER_PDF_AS_SEPARATE_IMAGES);
    const storedValue = this.localStorageService.getItem(
      LocalStorageItemIDs.PDF_UPLOAD_AS_SEPARATE_IMAGES,
    );

    if (!isFlagEnabled) {
      this.uploadAsSeparateImages = false;
    } else {
      this.uploadAsSeparateImages = storedValue !== 'false';
    }
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.bulkUploadService.files$.pipe(untilDestroyed(this)).subscribe((files) => {
        this.selectedFilesCount = files.filter(
          (file) => this.getSelectedPageCount(file) > 0,
        ).length;
        this.selectedPagesCount = files.reduce(
          (sum, file) => sum + this.getSelectedPageCount(file),
          0,
        );
        this.updateHeaderText();
        this.strategiesSelectionItems = this.generateSelectionItems();
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedStrategy'] || changes['uploadAsSeparateImages']) {
      this.emitSelection();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  itemSelected(selectedItemIndex: number) {
    this.selectedStrategy = Object.values(PDFBoardStrategy)[selectedItemIndex];
    this.emitSelection();
  }

  onUploadAsSeparateImagesChange() {
    this.localStorageService.setItem(
      LocalStorageItemIDs.PDF_UPLOAD_AS_SEPARATE_IMAGES,
      String(this.uploadAsSeparateImages),
    );
    this.emitSelection();
  }

  private updateHeaderText() {
    if (this.selectedFilesCount > 1) {
      const count = this.selectedFilesCount;
      this.headerText =
        count === 1
          ? this.translateService.instant('You are importing {{count}} file.', { count })
          : this.translateService.instant('You are importing {{count}} files.', { count });
      this.bodyText = this.translateService.instant('How should we import your files?');
      return;
    }

    const pagesCount = this.selectedPagesCount || 1;
    this.headerText = this.translateService.instant(
      pagesCount === 1
        ? 'You are importing {{pagesCount}} page.'
        : 'You are importing {{pagesCount}} pages.',
      { pagesCount },
    );
    this.bodyText = this.translateService.instant('How should we import your file?');
  }

  private generateSelectionItems(): SelectionItem[] {
    const isParticipant = this.spaceRepo.isCurrentUserSpaceParticipant;
    const isFollower = this.spaceLeaderModeService.currentUserIsBeingLed();
    const isBoardFoldersFlagEnabled = this.flagsService.isFlagEnabled(FLAGS.SPACES_BOARD_FOLDERS);
    const isSingleFile = this.selectedFilesCount === 1;

    return Object.values(PDFBoardStrategy).map((strategy) => {
      switch (strategy) {
        case PDFBoardStrategy.CurrentBoard:
          return {
            text: this.translateService.instant('Add to current board'),
            icon: {
              name: 'add_to_current_board',
              type: IconTypes.svgIcon,
            },
          };
        case PDFBoardStrategy.NewBoard:
          return {
            text: this.translateService.instant('Add to new board'),
            icon: {
              name: 'add_to_new_board',
              type: IconTypes.svgIcon,
            },
            disabledToolTipText: isParticipant
              ? this.translateService.instant('You should be a host to be able to use this option')
              : isFollower
              ? this.translateService.instant(
                  'You cannot import to a different board while the host is leading',
                )
              : null,
            disabled: isParticipant || isFollower,
          };
        case PDFBoardStrategy.OneBoardPerPage:
          return {
            text: isSingleFile
              ? this.translateService.instant('One page per board')
              : this.translateService.instant('One file per board'),
            icon: {
              name: 'one_page_per_board',
              type: IconTypes.svgIcon,
            },
            tooltipText: isSingleFile
              ? this.translateService.instant('Boards will be grouped \n in a folder')
              : undefined,
            disabledToolTipText: isParticipant
              ? this.translateService.instant('You should be a host to be able to use this option')
              : isFollower
              ? this.translateService.instant(
                  'You cannot import to a different board while the host is leading',
                )
              : null,
            disabled: isParticipant || isFollower,
            hidden: !isBoardFoldersFlagEnabled,
          };
      }
    });
  }

  private emitSelection() {
    this.selectionChange.emit({
      strategy: this.selectedStrategy,
      uploadAsSeparateImages: this.uploadAsSeparateImages,
    });
  }

  private getSelectedPageCount(file: { pages?: number[]; selectedPageIndices?: number[] }): number {
    if (file.selectedPageIndices) {
      return file.selectedPageIndices.length;
    }
    return file.pages?.length ?? 0;
  }

  protected readonly PDFBoardStrategy = PDFBoardStrategy;
  protected readonly Object = Object;
}
